import type { DocumentNode } from '@apollo/client';

import type {
  CrafterMultiWishlistQuery,
  CrafterSpecificWishlistQuery,
  CrafterWishlistNamesQuery
} from '@/__generated__/gateway/graphql';
import {
  addItemToWishlistMutation,
  createCrafterWishlistMutation,
  deleteCrafterWishlistMutation,
  removeItemFromWishlistMutation,
  updateCrafterWishlistMutation
} from '@/shared/graphql/gateway/mutations';
import {
  crafterMultiWishlistQuery,
  crafterSpecificWishlistQuery,
  crafterWishlistNamesQuery,
  publicCrafterWishlistQuery
} from '@/shared/graphql/gateway/queries';
import { getApolloClients } from '@/shared/utils/apollo-client';

// Type for public wishlist response (matches the actual backend GraphQL schema)
// Note: PublicWishlistItem only has sku and product_group_identifier (no id or created_at)
type PublicWishlistResponse = {
  crafter?: {
    publicWishlist?: {
      wishlist?: {
        id: string;
        name: string;
        description?: string | null;
        items?: {
          cursor?: string | null;
          limit?: number | null;
          items?: Array<{
            product_group_identifier?: string | null;
            sku?: string | null;
          }>;
        } | null;
        created_at: string;
        updated_at: string;
      } | null;
      cursor?: string | null;
      limit?: number | null;
    } | null;
  } | null;
};

// Constants for multi-wishlist query optimization
const MULTI_WISHLIST_LIMIT = 5;
/** Max items to request per wishlist (multi-wishlist and update mutation). */
export const MULTI_WISHLIST_ITEMS_LIMIT = 50;

export class WishlistApiService {
  /**
   * Creates a new wishlist
   * @param name - The name of the wishlist
   * @param description - Optional description for the wishlist
   * @param isPublic - Optional flag to set the wishlist as public (default is false)
   * @param cursor - Optional cursor for pagination of items
   * @param limit - Optional limit for items per page
   * @returns Promise resolving to the created wishlist
   */
  static async createWishlist(
    name: string,
    description?: string,
    isPublic?: boolean,
    cursor?: string | null,
    limit?: number | null
  ) {
    const { gateway } = await getApolloClients();
    const result = await gateway.mutate({
      mutation: createCrafterWishlistMutation,
      variables: {
        input: {
          name,
          description: description || undefined,
          public: isPublic || false
        },
        cursor: cursor ?? undefined,
        limit: limit ?? undefined
      }
    });

    const userErrors = result.data?.crafter?.wishlistCreate?.userErrors;
    if (userErrors && userErrors.length > 0) {
      throw new Error(userErrors[0] || 'Failed to create wishlist');
    }

    return result.data?.crafter?.wishlistCreate?.wishlist;
  }

  /**
   * Fetches all wishlists
   * @param sortBy - Optional sort field
   * @param sortDir - Optional sort direction
   * @param cursor - Optional cursor for pagination
   * @param limit - Optional limit for wishlists per page (default: 5)
   * @param itemsLimit - Optional limit for items per wishlist (default: 4)
   * @returns Promise resolving to wishlists response
   */
  static async fetchMultiWishlists(
    sortBy?: string,
    sortDir?: string,
    cursor?: string | null,
    limit: number = MULTI_WISHLIST_LIMIT,
    itemsLimit: number = MULTI_WISHLIST_ITEMS_LIMIT
  ) {
    const { gateway } = await getApolloClients();
    const result = await gateway.query<CrafterMultiWishlistQuery>({
      query: crafterMultiWishlistQuery,
      variables: {
        sortBy: sortBy || undefined,
        sortDir: sortDir || undefined,
        cursor: cursor || undefined,
        limit,
        itemsLimit2: itemsLimit
      },
      fetchPolicy: 'network-only'
    });

    return result.data?.crafter?.wishlists;
  }

  /**
   * Fetches a specific wishlist by ID
   * @param wishlistId - The ID of the wishlist to fetch
   * @param cursor - Optional cursor for pagination of items
   * @param limit - Optional limit for items per page
   * @returns Promise resolving to the wishlist
   */
  static async fetchSpecificWishlist(
    wishlistId: string,
    cursor?: string | null,
    limit?: number | null
  ) {
    const { gateway } = await getApolloClients();
    const result = await gateway.query<CrafterSpecificWishlistQuery>({
      query: crafterSpecificWishlistQuery,
      variables: {
        wishlistId,
        cursor: cursor || undefined,
        limit: limit || undefined
      },
      fetchPolicy: 'network-only'
    });

    return result.data?.crafter?.wishlist;
  }

  /**
   * Deletes a wishlist
   * @param wishlistId - The ID of the wishlist to delete
   * @returns Promise resolving to success status
   */
  static async deleteWishlist(
    wishlistId: string
  ): Promise<boolean | null | undefined> {
    const { gateway } = await getApolloClients();
    const result = await gateway.mutate({
      mutation: deleteCrafterWishlistMutation,
      variables: { wishlistDeleteId: wishlistId }
    });

    return result.data?.crafter?.wishlistDelete?.success;
  }

  /**
   * Updates a wishlist's name, description, or public status
   * @param wishlistId - The ID of the wishlist to update
   * @param input - Object containing optional name, description, and/or public status
   * @param cursor - Optional cursor for pagination of items
   * @param limit - Optional limit for items per page
   * @returns Promise resolving to the updated wishlist
   */
  static async updateWishlist(
    wishlistId: string,
    input: {
      name?: string | null;
      description?: string | null;
      public?: boolean | null;
    },
    cursor?: string | null,
    limit?: number | null
  ) {
    const { gateway } = await getApolloClients();
    const result = await gateway.mutate({
      mutation: updateCrafterWishlistMutation,
      variables: {
        wishlistUpdateId: wishlistId,
        input: {
          name: input.name ?? undefined,
          ...(input.description !== undefined && {
            description: input.description
          }),
          public: input.public ?? undefined
        },
        cursor: cursor ?? undefined,
        limit: limit ?? undefined
      }
    });

    const userErrors = result.data?.crafter?.wishlistUpdate?.userErrors;
    if (userErrors && userErrors.length > 0) {
      throw new Error(userErrors[0] || 'Failed to update wishlist');
    }

    return result.data?.crafter?.wishlistUpdate?.wishlist;
  }

  /**
   * Fetches all wishlist names (lightweight version without items)
   * @returns Promise resolving to wishlists with names and basic info
   */
  static async fetchWishlistNames() {
    const { gateway } = await getApolloClients();
    const result = await gateway.query<CrafterWishlistNamesQuery>({
      query: crafterWishlistNamesQuery,
      fetchPolicy: 'network-only'
    });

    return result.data?.crafter?.wishlists?.wishlists;
  }

  /**
   * Adds a product to a specific wishlist
   * @param wishlistId - The ID of the wishlist to add to
   * @param productGroupIdentifier - Optional product group identifier
   * @param sku - Optional SKU for specific variant
   * @returns Promise resolving to the added wishlist item or errors
   */
  static async addItemToWishlist(
    wishlistId: string,
    productGroupIdentifier?: string,
    sku?: string
  ) {
    const { gateway } = await getApolloClients();
    const result = await gateway.mutate({
      mutation: addItemToWishlistMutation,
      variables: { wishlistId, productGroupIdentifier, sku }
    });

    const response = result.data?.crafter?.wishlistAddItem;
    if (response?.userErrors && response.userErrors.length > 0) {
      throw new Error(
        response.userErrors[0] || 'Failed to add item to wishlist'
      );
    }

    return response?.wishlistItem;
  }

  /**
   * Removes an item from a specific wishlist
   * @param wishlistId - The ID of the wishlist
   * @param itemId - The ID of the item to remove
   * @returns Promise resolving to success status
   */
  static async removeItemFromWishlist(
    wishlistId: string,
    itemId: string
  ): Promise<boolean | null | undefined> {
    const { gateway } = await getApolloClients();
    const result = await gateway.mutate({
      mutation: removeItemFromWishlistMutation,
      variables: { wishlistId, itemId }
    });

    const response = result.data?.crafter?.wishlistRemoveItem;
    if (response?.userErrors && response.userErrors.length > 0) {
      throw new Error(
        response.userErrors[0] || 'Failed to remove item from wishlist'
      );
    }

    return response?.success;
  }

  /**
   * Fetches a public wishlist by its public ID (no authentication required)
   * @param publicId - The public ID of the wishlist to fetch
   * @param cursor - Optional cursor for pagination of items
   * @param limit - Optional limit for items per page
   * @returns Promise resolving to the public wishlist
   */
  static async fetchPublicWishlist(
    publicId: string,
    cursor?: string | null,
    limit?: number | null
  ) {
    const { gateway } = await getApolloClients();
    // Note: Query type assertion needed until graphql-codegen generates the proper types
    const result = await gateway.query({
      query: publicCrafterWishlistQuery as DocumentNode,
      variables: {
        id: publicId, // Backend uses 'id' as the argument name
        cursor: cursor || undefined,
        limit: limit || undefined
      },
      fetchPolicy: 'network-only'
    });

    // The wishlist data is nested inside publicWishlist.wishlist
    return (result.data as PublicWishlistResponse)?.crafter?.publicWishlist
      ?.wishlist;
  }
}
